import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { extractNumbers, splitStringByArray } from '../utils/stringUtils';
import { ReadOnlyLock } from './components/ReadOnlyLock';
import { Wrapper } from './components/Wrapper';
import { Input } from './components/Input';
import { Mask } from './components/Mask';
import { Delimiter } from './components/Delimiter';
var Group = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: relative;\n  flex: 0 0 auto;\n"], ["\n  position: relative;\n  flex: 0 0 auto;\n"])));
var iconClass = 'm-inputgroups__icon';
export var InputGroups = styled(function (_a) {
    var id = _a.id, groups = _a.groups, _b = _a.delimiter, delimiter = _b === void 0 ? '.' : _b, isRequired = _a.isRequired, isDisabled = _a.isDisabled, isReadOnly = _a.isReadOnly, isValid = _a.isValid, hasInfo = _a.hasInfo, hasError = _a.hasError, value = _a.value, onChange = _a.onChange, onBlur = _a.onBlur, onClick = _a.onClick, onFocus = _a.onFocus, onKeyDown = _a.onKeyDown, type = _a.type, inputMode = _a.inputMode, className = _a.className, children = _a.children, label = _a.label, wrapperRef = _a.wrapperRef, htmlAttributes = __rest(_a, ["id", "groups", "delimiter", "isRequired", "isDisabled", "isReadOnly", "isValid", "hasInfo", "hasError", "value", "onChange", "onBlur", "onClick", "onFocus", "onKeyDown", "type", "inputMode", "className", "children", "label", "wrapperRef"]);
    var inputRefs = groups.map(function () { return React.createRef(); });
    var _c = React.useState(value || groups.map(function () { return undefined; })), guardedValue = _c[0], setGuardedValue = _c[1];
    React.useEffect(function () {
        setGuardedValue(value);
    }, [value]);
    var getValue = function () {
        return inputRefs.map(function (_a) {
            var current = _a.current;
            return (current ? current.value : undefined);
        });
    };
    var onChangeHandler = function (groupIndex) { return function (evt) {
        var val = evt.target.value;
        var group = groups[groupIndex];
        var nextRef = inputRefs[groupIndex + 1];
        if (val.length === group.mask.length && nextRef && nextRef.current) {
            nextRef.current.focus();
        }
        // to prevent typing when the mask length is exceeded
        if (onChange && val.length <= group.mask.length)
            onChange(evt, getValue());
    }; };
    var onBlurHandler = function (evt) {
        return onBlur && onBlur(evt, getValue());
    };
    var onClickHandler = function (evt) {
        var target = evt.target;
        var firstRef = inputRefs[0];
        var targetInput = inputRefs.find(function (_a) {
            var current = _a.current;
            return current === target;
        });
        var firstFilledInput = inputRefs.find(function (_a) {
            var current = _a.current;
            return current ? !!current.value : false;
        });
        if (targetInput && targetInput.current && !!firstFilledInput)
            targetInput.current.focus();
        else if (firstRef && firstRef.current)
            firstRef.current.focus();
        if (onClick)
            onClick(evt);
    };
    var onFocusHandler = function (evt) {
        try {
            var target = evt.target;
            target.setSelectionRange(0, target.value.length);
            // eslint-disable-next-line
        }
        catch (err) { }
        if (onFocus)
            onFocus(evt);
    };
    var onPasteHandler = function (evt) {
        evt.preventDefault();
        var data = evt.clipboardData.getData('text');
        var groupsLengths = groups.map(function (_a) {
            var mask = _a.mask;
            return mask.length;
        });
        var newData = extractNumbers(data, type);
        return setGuardedValue(splitStringByArray(newData, groupsLengths));
    };
    var handleBackspaceKey = function (currentRef, previousRef, evt) {
        var _a, _b;
        if (((_a = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _a === void 0 ? void 0 : _a.value.length) === 0) {
            evt.preventDefault();
            (_b = previousRef === null || previousRef === void 0 ? void 0 : previousRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    };
    var handleArrowLeftKey = function (currentRef, previousRef, evt) {
        var _a, _b, _c;
        if (((_a = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart) === 0 &&
            ((_b = currentRef === null || currentRef === void 0 ? void 0 : currentRef.current) === null || _b === void 0 ? void 0 : _b.selectionEnd) === 0) {
            evt.preventDefault();
            (_c = previousRef === null || previousRef === void 0 ? void 0 : previousRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        }
    };
    var handleArrowRightKey = function (currentRef, nextRef, evt) {
        var _a;
        if (currentRef &&
            currentRef.current &&
            currentRef.current.selectionStart === currentRef.current.value.length &&
            currentRef.current.selectionEnd === currentRef.current.value.length) {
            evt.preventDefault();
            (_a = nextRef === null || nextRef === void 0 ? void 0 : nextRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    var hadnleAlphabeticalInput = function (evt) {
        if (type === 'number' && !evt.key.match(/^\d+$/)) {
            evt.preventDefault();
        }
    };
    var onKeyDownHandler = function (groupIndex) { return function (evt) {
        var currentRef = inputRefs[groupIndex];
        var previousRef = inputRefs[groupIndex - 1];
        var nextRef = inputRefs[groupIndex + 1];
        switch (evt.key) {
            case 'Backspace':
                handleBackspaceKey(currentRef, previousRef, evt);
                break;
            case 'Left':
            case 'ArrowLeft':
                handleArrowLeftKey(currentRef, previousRef, evt);
                break;
            case 'Right':
            case 'ArrowRight':
                handleArrowRightKey(currentRef, nextRef, evt);
                break;
            default:
                hadnleAlphabeticalInput(evt);
                break;
        }
        if (onKeyDown)
            onKeyDown(evt);
    }; };
    return (React.createElement(Wrapper, __assign({ role: "group", id: id + "-group", "aria-label": label, ref: wrapperRef }, {
        onClick: onClickHandler,
        onPaste: onPasteHandler,
        className: className,
        iconClass: iconClass,
        isDisabled: isDisabled,
        isReadOnly: isReadOnly,
        isValid: isValid,
        hasInfo: hasInfo,
        hasError: hasError,
    }),
        isReadOnly && React.createElement(ReadOnlyLock, { className: iconClass }),
        groups.map(function (_a, index) {
            var name = _a.name, mask = _a.mask, pattern = _a.pattern, title = _a.title, groupAttributes = __rest(_a, ["name", "mask", "pattern", "title"]);
            var groupId = index === 0 ? id : id + "-" + index;
            return (React.createElement(React.Fragment, { key: name },
                React.createElement(Group, null,
                    React.createElement(Input, __assign({ type: type, inputMode: inputMode }, htmlAttributes, groupAttributes, { ref: inputRefs[index], value: guardedValue[index], id: groupId, name: name, maxLength: mask.length, onChange: onChangeHandler(index), onBlur: onBlurHandler, onFocus: onFocusHandler, onKeyDown: onKeyDownHandler(index), disabled: isDisabled, readOnly: isReadOnly, required: isRequired, pattern: pattern, title: title, "aria-label": name })),
                    React.createElement(Mask, { value: guardedValue[index] || '', mask: mask })),
                index < groups.length - 1 && (React.createElement(Delimiter, { "aria-hidden": true }, delimiter))));
        })));
})(function () { return css(templateObject_2 || (templateObject_2 = __makeTemplateObject([""], [""]))); });
export var InputGroupPropTypes = {
    name: PropTypes.string.isRequired,
    mask: PropTypes.string.isRequired,
    pattern: PropTypes.string,
    title: PropTypes.string,
};
export var InputGroupsPropTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape(InputGroupPropTypes))
        .isRequired,
    delimiter: PropTypes.string,
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    isValid: PropTypes.bool,
    hasInfo: PropTypes.bool,
    hasError: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    type: PropTypes.string,
    inputMode: PropTypes.string,
    wrapperRef: PropTypes.exact({
        current: PropTypes.instanceOf(HTMLDivElement),
    }),
    className: PropTypes.string,
    children: PropTypes.node,
};
InputGroups.propTypes = InputGroupsPropTypes;
var templateObject_1, templateObject_2;
//# sourceMappingURL=InputGroups.js.map